// Copyright (c) Docugami, Inc. All rights reserved.

import { ITooltipHostStyles, TooltipHost } from "@fluentui/react";
import { dgFontFamily } from "components/common/Fonts";
import { selectDocumentsWithCpml } from "components/reports/AgGridReportTable";
import { appContentColor, emptyGray, greyText } from "components/shell/presentation/AppStyles";
import { useAppSelector } from 'hooks/redux';
import { ReportViewContext } from "networking/reportsApi";
import { ReactNode } from "react";
import { NormalizedState, ReportDocument, selectPublishedDocumentsView, selectReport, selectReviewDocumentsView } from "store/reports/selectors";
import styled, { css } from "styled-components";
import { ReportTableFilters } from "./ReportTableFilters";

const ContextTabsContainer = styled.div`
    display: flex;
    width: 100%;
    background-color: ${greyText};
    height: 50px;
    align-items: flex-end;
    gap: 3px;
    padding-top: 3px;
`

interface ContextSwitchButtonProps {
    currentContext?: ReportViewContext;
    onContextChange?: (context: ReportViewContext) => void;
}

export const ReportContextSwitcher = (props: ContextSwitchButtonProps) => {
    const report = useAppSelector(selectReport);
    const reviewDocumentsView = useAppSelector(selectReviewDocumentsView);
    const publishDocumentsView = useAppSelector(selectPublishedDocumentsView);
    const autoPublish = !!report?.autoPublishDocuments;
    const isPublishedMode = props.currentContext === ReportViewContext.Published;

    const numTotalDocuments = useAppSelector(state => selectDocumentsWithCpml(state, report?.clusterId ?? '')?.ids.length);

    const hasReviewDocuments = !!reviewDocumentsView?.ids.length;
    const hasNoPublishedDocuments = !!publishDocumentsView && !publishDocumentsView.ids.length;
    return !!report?.isPublished && !!props.currentContext && !autoPublish && props.onContextChange ? (
        <ContextTabsContainer>
            <ContextSwitcherTab
                key={ReportViewContext.Review}
                title={"manual confirm documents"}
                isActive={!isPublishedMode}
                onContextChange={() => props.onContextChange!(ReportViewContext.Review)}
                disabled={!hasReviewDocuments}
                tooltip={!hasReviewDocuments ? "You don't have any more documents to review. Check back when new documents are added to this docset." : ''}>
                <DocumentsCountInfo documentsView={reviewDocumentsView} totalCount={numTotalDocuments} />
            </ContextSwitcherTab>
            <ContextSwitcherTab
                key={ReportViewContext.Published}
                title={"published documents"}
                isActive={isPublishedMode}
                onContextChange={() => props.onContextChange!(ReportViewContext.Published)}
                disabled={hasNoPublishedDocuments}
                tooltip={hasNoPublishedDocuments ? "There are no published documents yet." : ''}>
                <DocumentsCountInfo documentsView={publishDocumentsView} totalCount={numTotalDocuments} />
            </ContextSwitcherTab>
        </ContextTabsContainer >
    ) : null;
}

interface DocumentsCountInfoProps {
    documentsView?: NormalizedState<ReportDocument>;
    totalCount?: number;
}

const DocumentsCountInfo = (props: DocumentsCountInfoProps): JSX.Element => {
    if (!props.documentsView) {
        return <></>;
    }

    return <DocumentsCountText>{`${props.documentsView.ids.length} ${!!props.totalCount ? `of ${props.totalCount} ` : ''}Docs`}</DocumentsCountText>
}

const ContextTabRoot = styled.div <{ isSelected: boolean, disabled?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 10px;
    box-sizing: border-box;
    background: white;

    border-left: 1px solid ${emptyGray};
    border-right: 1px solid ${emptyGray};
    border-radius: 10px 10px 0px 0px;

    font-weight: 800;

    width: 65%;
    transition: width ease-in-out 0.3s;

    ${props => !props.isSelected && !props.disabled && SelectableCss};
    ${props => props.disabled && DisabledCss};
`

const ContextTabContent = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;

    text-transform: uppercase;
    gap: 10px;
    font-family: ${dgFontFamily};

    .right-align {
        margin-right: 0px;
        margin-left: auto;
    }
`

const DisabledCss = css`
    opacity: .3;
    pointer-events: none;
    cursor: initial;
`;

const SelectableCss = css`
    cursor: pointer;
    border-bottom: 1px solid ${emptyGray};
    opacity: .5;

    font-style: italic;
    font-weight: 500;

    width: 35%;
    transition: width ease-in-out 0.3s, opacity ease-in-out .1s;

    :hover {
        opacity: .7;
    }
`;

const DocumentsCountText = styled.span`
    font-family: Lato;
    font-size: 18px;
    font-weight: 700;
    line-height: 21.6px;
    text-align: right;
    color: ${appContentColor};
    text-transform: initial;
`;

const tooltipStyles: Partial<ITooltipHostStyles> = {
    root: {
        width: '100%',
    }
}

interface ContextSwitcherTabProps {
    title: ReactNode;
    isActive: boolean;
    onContextChange: () => void;
    disabled?: boolean;
    tooltip?: string | JSX.Element | JSX.Element[];
    children?: ReactNode;
}

const ContextSwitcherTab = (props: ContextSwitcherTabProps): JSX.Element => {
    function getContent() {
        if (props.isActive) {
            return (
                <>
                    {props.title}
                    <ReportTableFilters />
                    <div className="right-align">
                        {props.children}
                    </div>
                </>
            )
        }
        else {
            return props.title;
        }
    }

    return (
        <ContextTabRoot
            disabled={props.disabled}
            onClick={() => props.onContextChange()}
            isSelected={props.isActive}>
            <TooltipHost
                styles={tooltipStyles}
                content={props.tooltip}>
                <ContextTabContent>
                    {getContent()}
                </ContextTabContent>
            </TooltipHost>
        </ContextTabRoot>
    )
}
